import { useState } from "react";
import { toast } from "sonner";
import { Gift } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useApi, errText } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "the end of this billing period");

export const CancelPlan = ({ me }) => {
  const api = useApi();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const run = async (kind, path, ok) => {
    setBusy(kind);
    try { const r = await api.post(path); toast.success(ok(r)); setOpen(false); await qc.invalidateQueries({ queryKey: ["me"] }); }
    catch (e) { toast.error(errText(e, "Something went wrong")); }
    setBusy(null);
  };
  const cancel = () => run("cancel", "/payments/cancel", (r) => `Cancelled. You keep access until ${fmt(r.period_end)}.`);
  const accept = () => run("offer", "/payments/save-offer/accept", (r) => `Done. $${r.credit.toFixed(2)} credit added, so your next month is free.`);
  const resume = () => run("resume", "/payments/resume", () => "Your plan will keep renewing.");

  if (!me?.has_subscription) return null;
  if (me.cancel_at_period_end) {
    return (
      <div data-testid="cancel-scheduled" className="mt-8 max-w-4xl flex flex-wrap items-center gap-3 rounded-xl border border-[#2A2A2A] bg-[#121212] px-5 py-4">
        <p className="text-sm flex-1 min-w-[240px] text-[#A3A3A3]">Your plan is cancelled and ends on <span className="text-white">{fmt(me.period_end)}</span>. You keep full access until then.</p>
        <button data-testid="resume-plan-btn" disabled={!!busy} onClick={resume}
          className="rounded-full bg-[#00FF9D] px-4 py-2 text-xs font-semibold text-[#050505] hover:bg-white transition-colors disabled:opacity-50">{busy ? "Saving…" : "Keep my plan"}</button>
      </div>
    );
  }
  return (
    <>
      <button data-testid="cancel-plan-btn" onClick={() => { setOpen(true); if (me.save_offer) api.post("/payments/save-offer/shown").catch(() => {}); }} className="mt-8 text-xs text-[#A3A3A3] underline underline-offset-4 hover:text-white transition-colors">Cancel subscription</button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent data-testid="cancel-dialog" className="bg-[#121212] border-[#2A2A2A] text-white">
          {me.save_offer ? (
            <>
              <Gift size={28} className="text-[#00FF9D]" />
              <DialogTitle className="font-display text-2xl">Before you go, have a month on us</DialogTitle>
              <DialogDescription data-testid="save-offer-text" className="text-[#A3A3A3]">
                Stay and your next month is free: we'll add a ${me.save_offer_credit?.toFixed(2)} credit that covers it automatically. This is a one-time offer.
              </DialogDescription>
            </>
          ) : (
            <>
              <DialogTitle className="font-display text-2xl">Cancel your subscription?</DialogTitle>
              <DialogDescription className="text-[#A3A3A3]">You keep access until {fmt(me.period_end)}. After that the app locks except Billing.</DialogDescription>
            </>
          )}
          <div className="mt-4 flex flex-wrap gap-3">
            {me.save_offer && (
              <button data-testid="save-offer-accept-btn" disabled={!!busy} onClick={accept}
                className="rounded-full bg-[#00FF9D] px-5 py-2.5 text-sm font-semibold text-[#050505] hover:bg-white transition-colors disabled:opacity-50">{busy === "offer" ? "Applying…" : "Stay and get one month free"}</button>
            )}
            <button data-testid="cancel-confirm-btn" disabled={!!busy} onClick={cancel}
              className="rounded-full border border-[#2A2A2A] px-5 py-2.5 text-sm text-[#A3A3A3] hover:text-white hover:border-white transition-colors disabled:opacity-50">{busy === "cancel" ? "Cancelling…" : me.save_offer ? "No thanks, cancel" : "Cancel subscription"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
